import { Message } from './chatbotApi';
import { EventEmitter } from 'events';
import { tokenRefreshService } from './tokenRefreshService';

export interface WebSocketMessage {
  type: string;
  data?: any;
  user_id?: number;
  username?: string;
  is_typing?: boolean;
  session?: any;
  messages?: any[];
  message?: string;
}

/**
 * Enhanced WebSocket service for chat functionality with automatic token refresh
 * 
 * Features:
 * - Automatic JWT token validation and refresh
 * - Seamless reconnection with fresh tokens
 * - Event-driven architecture for easy integration
 * - Built-in error handling and logging
 * 
 * Usage:
 * ```typescript
 * const chatService = new ChatWebSocketService();
 * 
 * // Listen for events
 * chatService.on('connect', ({ sessionId }) => console.log('Connected to session:', sessionId));
 * chatService.on('message', (data) => console.log('New message:', data));
 * chatService.on('token_refresh_failed', ({ error }) => console.error('Token refresh failed:', error));
 * 
 * // Connect to a chat session
 * await chatService.connect('session-uuid');
 * 
 * // Send messages
 * chatService.sendMessage('Hello, world!');
 * 
 * // Clean up when done
 * chatService.destroy();
 * ```
 */
export class ChatWebSocketService extends EventEmitter {
  private socket: WebSocket | null = null;
  private sessionId: string | null = null;
  private isConnecting: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private tokenRefreshTimeout: NodeJS.Timeout | null = null;
  private tokenCheckInterval: NodeJS.Timeout | null = null;

  constructor() {
    super();
    
    // Validate environment configuration
    const baseUrl = process.env.REACT_APP_BASE_URL;
    if (!baseUrl) {
      console.warn('⚠️ App Base URL not configured, using default');
      // console.warn('⚠️ REACT_APP_BASE_URL not configured, using default localhost:8000');
    }
    
    // Interval token monitoring - commented out as WebSocket close events handle token refresh
    // Uncomment if you need proactive token refresh every 60 seconds
    // this.setupTokenMonitoring();
  }

  // Commented out interval-based token monitoring
  // Uncomment these methods if you need proactive token refresh every 60 seconds
  /*
  private setupTokenMonitoring(): void {
    // Check token validity every minute
    this.tokenCheckInterval = setInterval(() => {
      this.checkAndRefreshTokenIfNeeded();
    }, 60000); // Check every minute
  }

  private async checkAndRefreshTokenIfNeeded(): Promise<void> {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.log('🔄 No token found, attempting to refresh...');
        await this.refreshTokenAndReconnect();
        return;
      }

      console.log('🔍 Checking token validity...');

      // Check if token is expired or about to expire (within 5 minutes)
      const tokenData = this.parseJwt(token);
      if (!tokenData) {
        console.log('🔄 Invalid token format, attempting to refresh...');
        await this.refreshTokenAndReconnect();
        return;
      }

      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = tokenData.exp - now;
      
      // If token expires in less than 5 minutes, refresh it
      if (timeUntilExpiry < 300) {
        console.log(`🔄 Token expires in ${timeUntilExpiry} seconds, refreshing...`);
        await this.refreshTokenAndReconnect();
      }
    } catch (error) {
      console.error('❌ Error checking token validity:', error);
    }
  }
  */

  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('❌ Error parsing JWT token:', error);
      return null;
    }
  }

  private async refreshTokenAndReconnect(): Promise<void> {
    try {
      // Only refresh if the token is actually expired — never rotate a valid token.
      if (!tokenRefreshService.shouldRefreshToken(0)) {
        console.log('🔄 [WebSocket] Token still valid, reconnecting without refresh...');
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          await this.reconnectWithNewToken();
        }
        return;
      }
      console.log('🔄 [WebSocket] Token expired, refreshing before reconnecting...');
      await tokenRefreshService.refreshToken(true);
      
      // Reconnect WebSocket with new token if connected
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        console.log('🔄 [WebSocket] Reconnecting with fresh token...');
        await this.reconnectWithNewToken();
      }
    } catch (error) {
      console.error('❌ [WebSocket] Failed to refresh token:', error);
      
      // Try to get more specific error information
      let errorMessage = 'Unknown error';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else {
        errorMessage = String(error);
      }
      
      // Emit error event so the UI can handle it (e.g., redirect to login)
      this.emit('token_refresh_failed', { error: errorMessage });
    }
  }

  private async reconnectWithNewToken(): Promise<void> {
    if (!this.sessionId) {
      console.log('❌ No session ID available for reconnection');
      return;
    }

    try {
      // Close existing connection
      if (this.socket) {
        this.socket.close(1000, 'Reconnecting with fresh token');
      }

      // Wait a bit before reconnecting
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reconnect with new token
      await this.connect(this.sessionId);
      console.log('✅ WebSocket reconnected with fresh token');
    } catch (error) {
      console.error('❌ Failed to reconnect WebSocket:', error);
    }
  }

  connect(sessionId: string, token?: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      if (this.isConnecting) {
        reject(new Error('Already connecting'));
        return;
      }

      this.isConnecting = true;
      this.sessionId = sessionId;

      // Always get the latest token from localStorage to ensure freshness
      let jwtToken = token || localStorage.getItem('access_token') || undefined;
      
      if (!jwtToken) {
        this.isConnecting = false;
        reject(new Error('No authentication token available'));
        return;
      }

      // Validate token before connecting
      const tokenData = this.parseJwt(jwtToken);
      if (!tokenData) {
        this.isConnecting = false;
        reject(new Error('Invalid token format'));
        return;
      }

      // Only refresh if the token is actually expired (timeUntilExpiry <= 0).
      // Do NOT refresh speculatively — a freshly-issued token (e.g. right after login)
      // must never trigger rotation here, as it causes an unnecessary blacklist+create cycle.
      const timeUntilExpiry = tokenData.exp - Math.floor(Date.now() / 1000);
      if (timeUntilExpiry <= 0) {
        console.log(`🔄 [WebSocket] Token has expired, refreshing before connecting...`);
        
        try {
          const refreshedToken = await tokenRefreshService.refreshToken(true);
          
          // Use the token returned from refresh (more reliable than reading from localStorage)
          jwtToken = refreshedToken;
          
          if (!jwtToken) {
            this.isConnecting = false;
            reject(new Error('Token refresh failed - no token available'));
            return;
          }
          
          // Re-validate the refreshed token
          const refreshedTokenData = this.parseJwt(jwtToken);
          if (!refreshedTokenData) {
            this.isConnecting = false;
            reject(new Error('Invalid token format after refresh'));
            return;
          }
          
          const refreshedTimeUntilExpiry = refreshedTokenData.exp - Math.floor(Date.now() / 1000);
          if (refreshedTimeUntilExpiry < 0) {
            this.isConnecting = false;
            reject(new Error('Refreshed token is still expired'));
            return;
          }
          
          console.log('✅ [WebSocket] Token refreshed successfully, proceeding with connection');
        } catch (refreshError) {
          console.error('❌ [WebSocket] Failed to refresh token:', refreshError);
          this.isConnecting = false;
          reject(new Error(`Token refresh failed: ${refreshError instanceof Error ? refreshError.message : 'Unknown error'}`));
          return;
        }
      }

      const wsUrl = this.getWebSocketUrl(sessionId, jwtToken);

      try {
        this.socket = new WebSocket(wsUrl);
        
        // Set a connection timeout
        const connectionTimeout = setTimeout(() => {
          if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
            this.socket.close();
            this.isConnecting = false;
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000); // 10 second timeout
        
        this.socket.onopen = () => {
          clearTimeout(connectionTimeout);
          console.log('WebSocket connected to session:', sessionId);
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.emit('connect', { sessionId });
          resolve();
        };

        this.socket.onmessage = (event) => {
          try {
            console.log('📥 [WebSocket] Raw message received:', event.data.substring(0, 200));
            const data: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('❌ [WebSocket] Error parsing WebSocket message:', error, event.data);
          }
        };

        this.socket.onclose = (event) => {
          clearTimeout(connectionTimeout);
          console.log('WebSocket disconnected:', event.code, event.reason);
          this.isConnecting = false;
          this.socket = null;
          this.emit('disconnect', { code: event.code, reason: event.reason });
          
          // Check if this was due to token expiry
          if (event.code === 1008 || event.reason?.includes('token') || event.reason?.includes('expired')) {
            console.log('🔄 [WebSocket] Closed due to token issues, attempting refresh...');
            // Don't await - let it run in background, but handle errors
            this.refreshTokenAndReconnect().catch((error) => {
              console.error('❌ [WebSocket] Background token refresh failed:', error);
              this.emit('token_refresh_failed', { error: error.message });
            });
            return;
          }
          
          // Only attempt to reconnect if not closed intentionally and WebSocket server exists
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            console.log('Attempting to reconnect...');
            this.attemptReconnect();
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.warn('Max reconnection attempts reached. WebSocket functionality disabled.');
            this.emit('error', { error: 'Max reconnection attempts reached' });
          }
        };

        this.socket.onerror = (error) => {
          clearTimeout(connectionTimeout);
          console.error('WebSocket error:', error);
          this.isConnecting = false;
          
          // Check if this is a connection error (server not available)
          if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
            console.warn('WebSocket server appears to be unavailable. Falling back to REST API only.');
            this.emit('error', { error: 'WebSocket server unavailable' });
            reject(new Error('WebSocket server unavailable'));
          } else {
            this.emit('error', { error });
            reject(error);
          }
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  private getWebSocketUrl(sessionId: string, token?: string): string {
    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';
    const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = baseUrl.replace(/^https?:\/\//, '');
    let wsUrl = `${wsProtocol}://${wsHost}/ws/chat/${sessionId}/`;
    
    if (token) {
      wsUrl += `?token=${encodeURIComponent(token)}`;
    }
    return wsUrl;
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts || !this.sessionId) {
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
    
    setTimeout(() => {
      if (this.sessionId) {
        this.connect(this.sessionId).catch(() => {
          console.log('Reconnect failed, will try again');
        });
      }
    }, delay);
  }

  private handleMessage(data: WebSocketMessage): void {
    console.log('🔌 [WebSocket] Received message from server:', {
      type: data.type,
      hasData: !!data.data,
      messageId: data.data?.id,
      isFromUser: data.data?.is_from_user
    });
    
    switch (data.type) {
      case 'message':
        console.log('📤 [WebSocket] Emitting message event to frontend:', data.data?.id);
        this.emit('message', data.data);
        break;
      case 'user_typing':
        this.emit('user_typing', {
          userId: data.user_id,
          username: data.username,
          isTyping: data.is_typing
        });
        break;
      case 'ai_typing':
        this.emit('ai_typing', { isTyping: data.is_typing });
        break;
      case 'session_info':
        this.emit('session_info', {
          session: data.session,
          messages: data.messages
        });
        break;
      case 'error':
        this.emit('error', { message: data.message });
        break;
      default:
        console.warn('Unknown message type:', data.type);
    }
  }

  sendMessage(content: string, messageType: string = 'text'): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected');
      return;
    }

    const message = {
      type: 'send_message',
      content,
      message_type: messageType
    };

    this.socket.send(JSON.stringify(message));
  }

  startTyping(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    this.socket.send(JSON.stringify({ type: 'typing_start' }));
  }

  stopTyping(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    this.socket.send(JSON.stringify({ type: 'typing_stop' }));
  }

  markAsRead(messageIds: string[]): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    this.socket.send(JSON.stringify({
      type: 'mark_read',
      message_ids: messageIds
    }));
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.close(1000, 'User disconnected');
      this.socket = null;
    }
    this.isConnecting = false;
    this.sessionId = null;
    this.reconnectAttempts = 0;
  }

  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  getConnectionState(): string {
    if (!this.socket) return 'disconnected';
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN: return 'connected';
      case WebSocket.CLOSING: return 'closing';
      case WebSocket.CLOSED: return 'closed';
      default: return 'unknown';
    }
  }

  // Cleanup method to be called when the service is no longer needed
  destroy(): void {
    // Clear intervals
    // Uncomment if you re-enable setupTokenMonitoring()
    /*
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
    */
    
    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout);
      this.tokenRefreshTimeout = null;
    }

    // Disconnect WebSocket
    this.disconnect();

    // Remove all event listeners
    this.removeAllListeners();
  }
}

export const chatWebSocket = new ChatWebSocketService();
